import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { IconHistory, IconPencil } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { Input } from "../../../../components/common/input";
import { DocumentsSection } from "../components/documents-section";
import { NotificationsSection } from "../components/notifications-section";
import { SectionCard, Field } from "../components/section-card";
import {
  CompanyProfileCompletionBanner,
  CompanyProfileEdit,
  CompanyProfileView,
} from "../components/company-profile-section";
import { useMe } from "../../../../swr";
import { ChangeRequestType } from "../../../../api";
import type { BankAccountDto, PendingChangeRequestSummaryDto } from "../../../../api";
import { useSubmitChangeRequest } from "../../hooks/useSubmitChangeRequest";
import { useMyPendingProfileChangeRequest } from "../../../change-requests/hooks/useMyPendingProfileChangeRequest";
import { useChangeRequestDetails } from "../../../change-requests/hooks/useChangeRequestDetails";
import {
  MAX_LENGTH,
  normalizeIban,
  validateAccountHolder,
  validateBankName,
  validateIban,
} from "../../../../utils/validation";

interface BankForm {
  bankName: string;
  accountHolder: string;
  iban: string;
}

type BankField = keyof BankForm;
type BankErrors = Partial<Record<BankField, string>>;

const BANK_FIELDS: BankField[] = ["bankName", "accountHolder", "iban"];

const BANK_LABELS: Record<BankField, string> = {
  bankName: "Bank name",
  accountHolder: "Account holder",
  iban: "IBAN",
};

const BANK_VALIDATORS: Record<BankField, (value: string) => string | null> = {
  bankName: validateBankName,
  accountHolder: validateAccountHolder,
  iban: validateIban,
};

/** The canonical (stored) form of a typed bank value. */
const normalizeBankValue = (field: BankField, value: string) =>
  field === "iban" ? normalizeIban(value) : value.trim();

/** The open bank-account request for the primary account (or a new one), if any. */
function findPendingBankRequest(
  reqs: PendingChangeRequestSummaryDto[] | undefined,
  bankAccountId: string | null,
): PendingChangeRequestSummaryDto | undefined {
  return reqs?.find((r) => {
    if (r.type === ChangeRequestType.BankAccountCreate) return bankAccountId === null;
    if (
      r.type === ChangeRequestType.BankAccountUpdate ||
      r.type === ChangeRequestType.BankAccountDelete
    ) {
      return bankAccountId != null && r.targetBankAccountId === bankAccountId;
    }
    return false;
  });
}

/** Text values of a bank change request's diff (`isDefault` etc. are left out). */
function proposedBankValues(proposedChanges: unknown): Partial<BankForm> {
  const proposed = (proposedChanges ?? {}) as Record<string, unknown>;
  const values: Partial<BankForm> = {};
  for (const field of BANK_FIELDS) {
    if (typeof proposed[field] === "string") values[field] = proposed[field] as string;
  }
  return values;
}

function PendingBadge() {
  return (
    <span className="ml-3 inline-flex items-center rounded-full bg-accentWarningTint px-2.5 py-0.5">
      <Text variant="bodyXs" tone="dark" className="text-accentWarning">
        Submitted for review
      </Text>
    </span>
  );
}

function isAccountComplete(input: {
  companyName?: string | null;
  crNumber?: string | null;
  taxNumber?: string | null;
  street?: string | null;
  city?: string | null;
  zip?: string | null;
  countryId?: string | null;
  stateId?: string | null;
  contactName?: string | null;
  bankName?: string | null;
  accountHolder?: string | null;
  iban?: string | null;
}): boolean {
  return !!(
    input.companyName &&
    input.crNumber &&
    input.taxNumber &&
    input.street &&
    input.city &&
    input.zip &&
    input.countryId &&
    input.contactName &&
    input.bankName &&
    input.accountHolder &&
    input.iban
  );
}

export function SellerSettingsPage() {
  const { user, mutate, isLoading } = useMe();
  const { submit, isLoading: isSubmittingBank } = useSubmitChangeRequest();
  const { data: pendingProfile } = useMyPendingProfileChangeRequest();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingBank, setIsEditingBank] = useState(false);

  const profilePending = !!pendingProfile;

  const primaryBank: BankAccountDto | null = useMemo(
    () => user?.bankAccounts?.[0] ?? null,
    [user?.bankAccounts],
  );

  const pendingBankRequest = findPendingBankRequest(
    user?.pendingChangeRequests,
    primaryBank?.id ?? null,
  );
  const bankPending = !!pendingBankRequest;
  // Submitted values aren't on the account until an admin approves them (the
  // seller's sign-up bank details included), so read them off the request.
  const { data: pendingBankDetails } = useChangeRequestDetails(pendingBankRequest?.id);
  const pendingBank = proposedBankValues(pendingBankDetails?.proposedChanges);

  const initialBank = useMemo<BankForm>(
    () => ({
      bankName: primaryBank?.bankName ?? "",
      accountHolder: primaryBank?.accountHolder ?? "",
      iban: primaryBank?.iban ?? "",
    }),
    [primaryBank?.bankName, primaryBank?.accountHolder, primaryBank?.iban],
  );

  const [bankForm, setBankForm] = useState<BankForm>(initialBank);
  const [bankErrors, setBankErrors] = useState<BankErrors>({});
  useEffect(() => setBankForm(initialBank), [initialBank]);

  const setBankField = (field: BankField, value: string) => {
    setBankForm((current) => ({ ...current, [field]: value }));
    setBankErrors((current) => ({ ...current, [field]: undefined }));
  };

  useEffect(() => {
    if (profilePending && isEditingProfile) setIsEditingProfile(false);
  }, [profilePending, isEditingProfile]);
  useEffect(() => {
    if (bankPending && isEditingBank) setIsEditingBank(false);
  }, [bankPending, isEditingBank]);

  const accountComplete = isAccountComplete({
    companyName: user?.companyName,
    crNumber: user?.crNumber,
    taxNumber: user?.taxNumber,
    street: user?.street,
    city: user?.city,
    zip: user?.zip,
    countryId: user?.country?.id?.toString() ?? null,
    stateId: user?.state?.id?.toString() ?? null,
    contactName: user?.contactName ?? user?.name ?? null,
    // Submitted bank details count: the seller has nothing left to fill in.
    bankName: initialBank.bankName || pendingBank.bankName,
    accountHolder: initialBank.accountHolder || pendingBank.accountHolder,
    iban: initialBank.iban || pendingBank.iban,
  });

  const handleBankCancel = () => {
    setBankForm(initialBank);
    setBankErrors({});
    setIsEditingBank(false);
  };

  const handleBankSave = async () => {
    // A new account needs every field; an update is a diff of the changed ones.
    const fields = primaryBank
      ? BANK_FIELDS.filter(
          (f) => normalizeBankValue(f, bankForm[f]) !== normalizeBankValue(f, initialBank[f]),
        )
      : BANK_FIELDS;

    if (fields.length === 0) {
      setIsEditingBank(false);
      return;
    }

    const errors: BankErrors = {};
    for (const f of fields) errors[f] = BANK_VALIDATORS[f](bankForm[f]) ?? undefined;
    setBankErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    const changes = Object.fromEntries(
      fields.map((f) => [f, normalizeBankValue(f, bankForm[f])]),
    );

    try {
      if (primaryBank) {
        await submit({
          type: ChangeRequestType.BankAccountUpdate,
          targetBankAccountId: primaryBank.id,
          proposedChanges: changes,
        });
      } else {
        await submit({
          type: ChangeRequestType.BankAccountCreate,
          proposedChanges: { ...changes, isDefault: true },
        });
      }
      await mutate();
      setBankForm(initialBank);
      setIsEditingBank(false);
    } catch {
      // toast already shown by useSubmitChangeRequest
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 px-8 py-6 max-md:px-4">
        <Text variant="body" tone="muted">Loading…</Text>
      </div>
    );
  }

  const showProfileEditButton = !isEditingProfile && !profilePending;

  return (
    <div className="flex flex-col gap-6 px-8 py-6 max-md:px-4">
      <div className="flex items-center justify-between max-md:flex-col max-md:items-start max-md:gap-2">
        <Heading variant="h2" tone="dark">Account Settings</Heading>
        <div className="flex items-center gap-3 max-md:flex-wrap">
          <Link
            to={AppRoutes.CHANGE_REQUESTS}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 max-md:-ml-3 text-textBody transition-colors hover:bg-bgWhite hover:text-textDark"
          >
            <IconHistory size={16} className="shrink-0" />
            <Text variant="bodySm" tone="inherit">Change requests</Text>
          </Link>
          {showProfileEditButton && (
            <Button
              variant="secondaryDark"
              size="sm"
              leftIcon={<IconPencil size={16} />}
              className="!bg-bgWhite"
              onClick={() => setIsEditingProfile(true)}
            >
              Edit profile
            </Button>
          )}
        </div>
      </div>

      <DocumentsSection showExpiryWarning={!isEditingProfile} />

      {!accountComplete && !profilePending && !isEditingProfile && (
        <CompanyProfileCompletionBanner onEdit={() => setIsEditingProfile(true)} />
      )}

      {isEditingProfile ? (
        <CompanyProfileEdit
          type={ChangeRequestType.SellerProfileUpdate}
          onCancel={() => setIsEditingProfile(false)}
          onSubmitted={() => setIsEditingProfile(false)}
        />
      ) : (
        <CompanyProfileView />
      )}

      <SectionCard
        title={
          <span className="flex items-center">
            Bank &amp; payment
            {bankPending && <PendingBadge />}
          </span>
        }
        action={
          !isEditingBank && !bankPending ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<IconPencil size={16} />}
              onClick={() => setIsEditingBank(true)}
            >
              Edit
            </Button>
          ) : isEditingBank ? (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBankCancel}
                disabled={isSubmittingBank}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleBankSave}
                disabled={isSubmittingBank}
              >
                {isSubmittingBank ? "Submitting…" : "Submit for review"}
              </Button>
            </div>
          ) : undefined
        }
      >
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          {isEditingBank ? (
            <>
              <Input
                label="Bank name"
                placeholder="e.g. Al Rajhi Bank"
                value={bankForm.bankName}
                maxLength={MAX_LENGTH.bankName}
                error={bankErrors.bankName}
                required
                onChange={(e) => setBankField("bankName", e.target.value)}
              />
              <Input
                label="Account holder"
                placeholder="Name on the account"
                value={bankForm.accountHolder}
                maxLength={MAX_LENGTH.accountHolder}
                error={bankErrors.accountHolder}
                required
                onChange={(e) => setBankField("accountHolder", e.target.value)}
              />
              <Input
                label="IBAN"
                placeholder="e.g. SA03 8000 0000 6080 1016 7519"
                value={bankForm.iban}
                maxLength={MAX_LENGTH.ibanInput}
                error={bankErrors.iban}
                required
                onChange={(e) => setBankField("iban", e.target.value)}
              />
            </>
          ) : (
            BANK_FIELDS.map((field) => {
              const live = initialBank[field];
              const proposed = pendingBank[field];
              return (
                <Field
                  key={field}
                  label={BANK_LABELS[field]}
                  // No approved account yet: what was submitted is all there is
                  // to show (the header says it's in review). Otherwise the live
                  // value stays, with the proposed one noted underneath.
                  value={(primaryBank ? live : proposed) || "—"}
                  pending={primaryBank && proposed !== live ? proposed : undefined}
                />
              );
            })
          )}
        </div>
      </SectionCard>

      <NotificationsSection />
    </div>
  );
}

export default SellerSettingsPage;
